import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface SlaEvent {
  eventType: 'STARTED' | 'PAUSED' | 'RESUMED' | 'RESET' | 'CANCELLED' | 'COMPLETED' | 'BREACHED';
  entityType: string;
  entityId: string;
  instanceId: string;
  policyName: string;
  targetType: string;
  timestamp: string;
}

/**
 * Hook to subscribe to real-time SLA events via Server-Sent Events (SSE)
 * Automatically invalidates SLA queries when events are received
 */
export function useSlaEvents(entityType: string, entityId: string | undefined) {
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!entityId) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    // Get the API URL from environment or default
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002/api/v1';
    const sseUrl = `${apiUrl}/sla-events/sse/${entityType}/${entityId}`;

    // Create EventSource with credentials
    // Note: SSE doesn't support custom headers, so we use query param for auth
    const eventSource = new EventSource(`${sseUrl}?token=${token}`);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      console.log(`[SSE] Connected to SLA events for ${entityType}/${entityId}`);
    };

    // Handle SLA update events
    eventSource.addEventListener('sla-update', (event) => {
      try {
        const data: SlaEvent = JSON.parse(event.data);
        console.log(`[SSE] SLA event received: ${data.eventType} - ${data.policyName}`);

        // Invalidate the SLA instances query to trigger a refetch
        queryClient.invalidateQueries({
          queryKey: ['sla-instances', entityType, entityId]
        });
      } catch (error) {
        console.error('[SSE] Error parsing SLA event:', error);
      }
    });

    eventSource.onerror = (error) => {
      console.error('[SSE] Connection error:', error);
      // EventSource will automatically try to reconnect
    };

    // Cleanup on unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        console.log(`[SSE] Disconnected from SLA events for ${entityType}/${entityId}`);
      }
    };
  }, [entityType, entityId, queryClient]);
}
